import {Quote as SdkQuote} from '@sportngin/se-commerce-sdk';
import {requestDetailsToRequestDetailsDto} from '../../common/context-data';
import {CartComparator} from './cart-comparator';
import {FactoryFn} from './factory-function';
import {
  participantDtoToPersona,
  Persona,
  personaToParticipantDto,
  personaToRegistrantDto,
  registrantDtoToPersona,
} from './persona';
import {RequestDetails} from './request-details';
import {RequestDetails as IRequestDetails} from '../../common/context-data';

export class QuoteParams implements CartComparator<QuoteParams> {
  provider: string;
  participant: Persona;
  registrant: Persona;
  requestDetails: RequestDetails;

  constructor(source?: {
    participant: Persona;
    registrant: Persona;
    requestDetails: IRequestDetails;
    provider: string;
  }) {
    if (!source) return;

    this.participant = new Persona(source.participant);
    this.registrant = new Persona(source.registrant);
    this.requestDetails = new RequestDetails(source.requestDetails);
    this.provider = source.provider;
  }

  /**
   * Determines equality based off the participant, intended to be used when testing the equality of a service
   * being added to the cart.
   * @param a the QuoteParams being compared
   * @param b this
   */
  equals(a: QuoteParams, b: QuoteParams = this): boolean {
    if (!a || a === b) return true;
    const isAps = b.provider.toLowerCase() === 'aps';
    const personaEqual = isAps
      ? a.registrant.equals(b.registrant)
      : a.participant.equals(b.participant);
    return personaEqual && a.requestDetails.equals(b.requestDetails);
  }
}

/**
 * ProductDisplayOverrides are used to override the default display fields for a ServiceOffer.
 */
export interface ProductDisplayOverrides {
  headline?: string;
  description?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  promotional_description?: string;
  disclaimer?: string;
  perils?: string[];
  recommended?: boolean;
}

export class Quote implements CartComparator<Quote> {
  id: string;
  provider: string;
  cost: number;
  request: QuoteParams;
  displayOverrides?: ProductDisplayOverrides;

  constructor(source?: {
    id: string;
    provider: string;
    cost: number;
    request: QuoteParams;
    displayOverrides?: ProductDisplayOverrides;
  }) {
    if (!source) return;

    this.id = source.id;
    this.provider = source.provider;
    this.cost = source.cost;
    this.request = source.request;
    this.displayOverrides = source.displayOverrides
      ? {...source.displayOverrides}
      : undefined;
  }

  /**
   * Determines equality based on criteria that are specific for adding an offer to the cart.
   * Returns true or false to indicate item equality.
   * @param a the Quote being compared to
   * @param b this
   */
  equals(a: Quote, b: Quote = this): boolean {
    let result = !a || a === b;
    if (!result) {
      result = a.provider === b.provider && a.request.equals(b.request);
    }
    return result;
  }
}

export const quoteToSdkQuote: FactoryFn<Quote, SdkQuote> = (source: Quote) => {
  const result: SdkQuote = {
    id: source.id,
    provider: source.provider,
    cost: source.cost,
    request: {
      registrant: personaToRegistrantDto(source.request.registrant),
      participant: personaToParticipantDto(source.request.participant),
      requestDetails: {...source.request.requestDetails},
    },
    productDisplayOverrides: source.displayOverrides
      ? {
          ...source.displayOverrides,
        }
      : undefined,
  };
  return result;
};

export const sdkQuoteToQuote: FactoryFn<SdkQuote, Quote | undefined> = (
  source: SdkQuote,
): Quote | undefined => {
  return source
    ? new Quote({
        id: `${source.id}`,
        cost: source.cost,
        provider: source.provider,
        request: new QuoteParams({
          registrant: registrantDtoToPersona(source.request.registrant),
          participant: participantDtoToPersona(source.request.participant),
          requestDetails: requestDetailsToRequestDetailsDto(
            source.request.requestDetails,
          ),
          provider: source.provider,
        }),
        displayOverrides: source.productDisplayOverrides
          ? {
              ...source.productDisplayOverrides,
            }
          : undefined,
      })
    : undefined;
};
